import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    startTransition,
} from 'react';
import { pushEvent } from '../core/dispatcher';
import { createEventGroupId } from '../utils/createEventGroupId';
import type { EventName, EventPayloadMap, PushOptions } from '../core/types';

type Interaction = {
    id: string;
    type?: string;
};

export type PushFn = (
    name: EventName,
    payload?: EventPayloadMap[EventName] | null,
    opts?: PushOptions
) => Promise<void>;

export type TrackingContextValue = {
    interaction: Interaction | null;
    startInteraction: (type?: string) => Interaction;
    endInteraction: () => void;
    push: PushFn;
};

const TrackingContext = createContext<TrackingContextValue | null>(null);

// Stores the current interaction ID outside of React state.
// Needed because state updates can be delayed, but tracking runs immediately.
// Ensures correct event grouping and override behavior.
let __activeInteractionId: string | undefined;
export function getActiveInteractionId(): string | undefined {
    return __activeInteractionId;
}

export function TrackingProvider({
                                     children,
                                     defaultOptions,
                                     autoEndInteraction = true,
                                     interactionEndOffsetMs = 100,
                                 }: {
    children: React.ReactNode;
    defaultOptions?: PushOptions;
    autoEndInteraction?: boolean;
    interactionEndOffsetMs?: number;
}) {
    const [interaction, setInteractionState] = useState<Interaction | null>(null);
    const interactionRef = useRef<Interaction | null>(null);
    const endTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const setInteraction = useCallback((value: Interaction | null) => {
        interactionRef.current = value;
        __activeInteractionId = value?.id;

        startTransition(() => {
            setInteractionState(value);
        });
    }, []);

    const startInteraction = useCallback((type?: string) => {
        const id =
            typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
                ? crypto.randomUUID()
                : createEventGroupId('interaction');

        const nextInteraction: Interaction = { id, type };

        if (endTimerRef.current) {
            clearTimeout(endTimerRef.current);
            endTimerRef.current = null;
        }

        setInteraction(nextInteraction);
        return nextInteraction;
    }, [setInteraction]);

    const endInteraction = useCallback(() => {
        if (endTimerRef.current) {
            clearTimeout(endTimerRef.current);
            endTimerRef.current = null;
        }
        __activeInteractionId = undefined;
        setInteraction(null);
    }, [setInteraction]);

    const scheduleEndInteraction = useCallback((ms: number) => {
        if (!autoEndInteraction) return;
        if (endTimerRef.current) {
            clearTimeout(endTimerRef.current);
            endTimerRef.current = null;
        }
        endTimerRef.current = setTimeout(() => {
            endInteraction();
        }, Math.max(0, ms + interactionEndOffsetMs));
    }, [autoEndInteraction, interactionEndOffsetMs, endInteraction]);

    const push = useCallback<PushFn>(
        async (name, payload, opts) => {
            const activeInteraction = interactionRef.current;

            const effectiveBufferMs =
                typeof opts?.bufferMs === 'number'
                    ? opts.bufferMs
                    : typeof defaultOptions?.bufferMs === 'number'
                        ? defaultOptions.bufferMs
                        : 1500;

            await pushEvent(name, payload, {
                ...defaultOptions,
                ...opts,
                eventGroupId: opts?.eventGroupId ?? activeInteraction?.id,
            });

            if (activeInteraction && typeof effectiveBufferMs === 'number') {
                scheduleEndInteraction(effectiveBufferMs);
            }
        },
        [defaultOptions, scheduleEndInteraction]
    );

    const value = useMemo<TrackingContextValue>(
        () => ({
            interaction,
            startInteraction,
            endInteraction,
            push,
        }),
        [interaction, startInteraction, endInteraction, push]
    );

    return <TrackingContext.Provider value={value}>{children}</TrackingContext.Provider>;
}

export function useTrackingGroup(): TrackingContextValue {
    const ctx = useContext(TrackingContext);
    if (!ctx) {
        throw new Error('useTrackingGroup must be used within a TrackingProvider');
    }
    return ctx;
}